import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { firestore } from '../Firebase';
import toast from 'react-hot-toast';
import { debounce } from 'lodash';

const DynamicBoxEditor = ({
  onContentChange,
  initialContent = '',
  videoID,
}) => {
  // Main states
  const [content, setContent] = useState(initialContent);
  const [slides, setSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSlideId, setActiveSlideId] = useState(null);

  // Use refs to prevent unnecessary re-renders and preserve values between renders
  const contentRef = useRef(content);
  const videoIDRef = useRef(videoID);
  const onContentChangeRef = useRef(onContentChange);
  const slidesRef = useRef(slides);
  const currentSlideIndexRef = useRef(currentSlideIndex);
  const activeSlideIdRef = useRef(activeSlideId);

  // Update refs when props/state change
  useEffect(() => {
    videoIDRef.current = videoID;
    onContentChangeRef.current = onContentChange;
    slidesRef.current = slides;
    currentSlideIndexRef.current = currentSlideIndex;
    activeSlideIdRef.current = activeSlideId;
  }, [videoID, onContentChange, slides, currentSlideIndex, activeSlideId]);

  // Create a stable debounced function for updating the preview
  const debouncedContentChange = useMemo(
    () => debounce((newContent) => {
      if (onContentChangeRef.current) {
        onContentChangeRef.current(newContent);
      }
    }, 500),
    []
  );

  // Keep contentRef updated with the latest content
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const updatePreviewImmediately = useCallback((newContent, slideId = null) => {
    debouncedContentChange.cancel();
    const selectedSlideId =
      slideId !== null
        ? slideId
        : slidesRef.current[currentSlideIndexRef.current]?.id || '';
    if (onContentChangeRef.current) {
      onContentChangeRef.current(newContent, {
        slideId: selectedSlideId,
        isActive: Boolean(
          selectedSlideId && selectedSlideId === activeSlideIdRef.current
        ),
      });
    }
  }, [debouncedContentChange]);

  useEffect(() => {
    return () => {
      debouncedContentChange.cancel();
    };
  }, [debouncedContentChange]);

  // Set up Firebase listeners and handle cleanup
  useEffect(() => {
    if (!videoID) return;

    let isMounted = true;
    setIsLoading(true);

    // One-time fetch of data instead of real-time listeners
    const loadInitialData = async () => {
      try {
        // Get video document for activeSlideId
        const videoDoc = await firestore
          .collection('videos')
          .doc(videoID)
          .get();
        const videoData = videoDoc.data() || {};
        const activeId = videoData.activeSlideId || null;

        if (isMounted) {
          setActiveSlideId(activeId);
        }

        // Get slides collection data
        const slidesSnapshot = await firestore
          .collection('videos')
          .doc(videoID)
          .collection('dynamicbox')
          .orderBy('createdAt', 'desc')
          .get();

        if (!isMounted) return;

        const slidesList = slidesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        }));

        setSlides(slidesList);

        // Handle initial slide selection
        if (slidesList.length > 0) {
          const activeIndex = activeId
            ? slidesList.findIndex((s) => s.id === activeId)
            : 0;
          const useIndex = activeIndex >= 0 ? activeIndex : 0;

          setCurrentSlideIndex(useIndex);
          const selectedSlide = slidesList[useIndex];
          const slideContent = selectedSlide?.content || '';
          setContent(slideContent);
          contentRef.current = slideContent;

          updatePreviewImmediately(slideContent, selectedSlide?.id || '');
        } else {
          setContent(initialContent || '');
          contentRef.current = initialContent || '';
          updatePreviewImmediately(initialContent || '');
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading initial data:', error);
        if (isMounted) {
          toast.error('Error loading slides');
          setIsLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [videoID, initialContent, updatePreviewImmediately]);


  // Handle form input change
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    contentRef.current = newContent;

    updatePreviewImmediately(newContent);
  };

  // Create a new slide
  const handleNewSlide = async () => {
    if (!videoIDRef.current) {
      toast.error('Video ID is required');
      return;
    }

    try {
      setIsLoading(true);

      // Prepare the new slide data
      const newSlideData = {
        content: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Create new slide in Firestore
      const newSlideRef = await firestore
        .collection('videos')
        .doc(videoIDRef.current)
        .collection('dynamicbox')
        .add(newSlideData);

      // Add to local state
      const newSlide = {
        id: newSlideRef.id,
        ...newSlideData,
      };

      // Update local state
      const updatedSlides = [newSlide, ...slides];
      setSlides(updatedSlides);
      setCurrentSlideIndex(0);
      setContent('');
      contentRef.current = '';

      // Notify parent
      updatePreviewImmediately('', newSlide.id);

      toast.success('New slide created');
    } catch (error) {
      console.error('Error creating slide:', error);
      toast.error(
        'Failed to create new slide: ' + (error.message || 'Unknown error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Save current slide
  const handleSaveSlide = async () => {
    if (!videoIDRef.current) {
      toast.error('Video ID is required');
      return;
    }

    try {
      setIsLoading(true);
      // Use contentRef to ensure we have the latest content
      const currentContent = contentRef.current;

      if (slides.length === 0 || !slides[currentSlideIndex]?.id) {
        // Create new slide
        const newSlideData = {
          content: currentContent,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        const newSlideRef = await firestore
          .collection('videos')
          .doc(videoIDRef.current)
          .collection('dynamicbox')
          .add(newSlideData);

        // Fetch the updated slides list
        const slidesSnapshot = await firestore
          .collection('videos')
          .doc(videoIDRef.current)
          .collection('dynamicbox')
          .orderBy('createdAt', 'desc')
          .get();

        const updatedSlides = slidesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        }));

        setSlides(updatedSlides);
        // Find the index of our newly created slide
        const newIndex = updatedSlides.findIndex(
          (s) => s.id === newSlideRef.id
        );
        setCurrentSlideIndex(newIndex >= 0 ? newIndex : 0);

        toast.success('New slide saved');
      } else {
        // Update existing slide
        const slideId = slides[currentSlideIndex].id;

        await firestore
          .collection('videos')
          .doc(videoIDRef.current)
          .collection('dynamicbox')
          .doc(slideId)
          .set({
            content: currentContent,
            updatedAt: new Date(),
          }, { merge: true });

        // Update in local state
        const updatedSlides = [...slides];
        updatedSlides[currentSlideIndex] = {
          ...updatedSlides[currentSlideIndex],
          content: currentContent,
          updatedAt: new Date(),
        };
        setSlides(updatedSlides);

        toast.success('Slide saved');
      }
    } catch (error) {
      console.error('Error saving slide:', error);
      toast.error(
        'Failed to save slide: ' + (error.message || 'Unknown error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Make current slide active (published)
  const handlePublish = async () => {
    if (
      !videoIDRef.current ||
      slides.length === 0 ||
      !slides[currentSlideIndex]?.id
    ) {
      toast.error('No slide to publish');
      return;
    }

    try {
      setIsLoading(true);
      // Use contentRef to ensure we have the latest content
      const currentContent = contentRef.current;

      // First save the current content
      const slideId = slides[currentSlideIndex].id;

      // Update slide content
      await firestore
        .collection('videos')
        .doc(videoIDRef.current)
        .collection('dynamicbox')
        .doc(slideId)
        .set({
          content: currentContent,
          updatedAt: new Date(),
        }, { merge: true });

      // Set as active slide
      await firestore.collection('videos').doc(videoIDRef.current).set({
        activeSlideId: slideId,
        dynamicBox: currentContent, // For backward compatibility
      }, { merge: true });

      // Update local state
      setActiveSlideId(slideId);

      // Update slides array with latest content
      const updatedSlides = [...slides];
      updatedSlides[currentSlideIndex] = {
        ...updatedSlides[currentSlideIndex],
        content: currentContent,
        updatedAt: new Date(),
      };
      setSlides(updatedSlides);

      // Notify parent with the most current content
      updatePreviewImmediately(currentContent, slideId);

      toast.success('Slide published');
    } catch (error) {
      console.error('Error publishing slide:', error);
      toast.error(
        'Failed to publish slide: ' + (error.message || 'Unknown error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate to previous slide
  const handlePrevious = () => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      const selectedSlide = slides[newIndex];
      const slideContent = selectedSlide?.content || '';

      setCurrentSlideIndex(newIndex);
      setContent(slideContent);
      contentRef.current = slideContent;

      // Notify parent
      updatePreviewImmediately(slideContent, selectedSlide?.id || '');
    }
  };

  // Navigate to next slide
  const handleNext = () => {
    if (currentSlideIndex < slides.length - 1) {
      const newIndex = currentSlideIndex + 1;
      const selectedSlide = slides[newIndex];
      const slideContent = selectedSlide?.content || '';

      setCurrentSlideIndex(newIndex);
      setContent(slideContent);
      contentRef.current = slideContent;

      // Notify parent
      updatePreviewImmediately(slideContent, selectedSlide?.id || '');
    }
  };

  // Delete current slide
  const handleDelete = async () => {
    if (
      !videoIDRef.current ||
      slides.length === 0 ||
      !slides[currentSlideIndex]?.id
    ) {
      return;
    }

    const slideId = slides[currentSlideIndex].id;

    // Can't delete active slide
    if (slideId === activeSlideId) {
      toast.error(
        "Can't delete the active slide. Publish another slide first."
      );
      return;
    }

    try {
      setIsLoading(true);

      // Delete from Firestore
      await firestore
        .collection('videos')
        .doc(videoIDRef.current)
        .collection('dynamicbox')
        .doc(slideId)
        .delete();

      // Update local state
      const updatedSlides = slides.filter(
        (_, index) => index !== currentSlideIndex
      );

      if (updatedSlides.length === 0) {
        // No slides left
        setSlides([]);
        setContent('');
        contentRef.current = '';
        setCurrentSlideIndex(0);

        // Notify parent
        updatePreviewImmediately('', '');
      } else {
        // Navigate to appropriate slide
        const newIndex =
          currentSlideIndex >= updatedSlides.length
            ? updatedSlides.length - 1
            : currentSlideIndex;

        const selectedSlide = updatedSlides[newIndex];
        const slideContent = selectedSlide?.content || '';

        setSlides(updatedSlides);
        setCurrentSlideIndex(newIndex);
        setContent(slideContent);
        contentRef.current = slideContent;

        // Notify parent
        updatePreviewImmediately(slideContent, selectedSlide?.id || '');
      }

      toast.success('Slide deleted');
    } catch (error) {
      console.error('Error deleting slide:', error);
      toast.error(
        'Failed to delete slide: ' + (error.message || 'Unknown error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Insert template into content
  const handleInsertTemplate = (template) => {
    const newContent = content ? `${content}\n\n${template}` : template;
    setContent(newContent);
    contentRef.current = newContent;

    // Use debounced function to notify parent
    debouncedContentChange(newContent);
  };

  return (
    <div className="dynamic-box-editor">
      <div className="editor-container">
        <h3>Content Editor</h3>

        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={handleNewSlide}
            disabled={isLoading}
          >
            New Slide
          </button>
          <button
            className="btn btn-sm btn-outline-secondary dynamic-box-arrow-btn me-2"
            onClick={handlePrevious}
            disabled={
              isLoading || currentSlideIndex <= 0 || slides.length === 0
            }
          >
            ←
          </button>
          <button
            className="btn btn-sm btn-outline-secondary dynamic-box-arrow-btn me-2"
            onClick={handleNext}
            disabled={
              isLoading ||
              currentSlideIndex >= slides.length - 1 ||
              slides.length === 0
            }
          >
            →
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleDelete}
            disabled={
              isLoading ||
              slides.length === 0 ||
              !slides[currentSlideIndex]?.id ||
              slides[currentSlideIndex]?.id === activeSlideId
            }
          >
            Delete
          </button>
        </div>

        <div className="dynamic-box-slide-status mb-3">
          <span className="badge bg-secondary me-2">
            Slide {slides.length > 0 ? currentSlideIndex + 1 : 0} of{' '}
            {slides.length}
          </span>
          {slides[currentSlideIndex]?.id === activeSlideId && (
            <span className="dynamic-box-active-label">Active</span>
          )}
        </div>

        <div className="mb-2">
          <button
            className="btn btn-sm btn-secondary me-2"
            onClick={handleSaveSlide}
            disabled={isLoading || !content.trim()}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={handlePublish}
            disabled={
              isLoading || slides.length === 0 || !slides[currentSlideIndex]?.id
            }
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Publishing...
              </>
            ) : (
              'Publish'
            )}
          </button>
        </div>

        <form onSubmit={(e) => e.preventDefault()}>
          <textarea
            value={content}
            onChange={handleContentChange}
            placeholder="Type your content here..."
            rows={10}
            className="content-editor"
            disabled={isLoading}
          />
        </form>

        <div className="dynamic-box-template-buttons mt-2">
          <button
            className="btn btn-sm btn-outline-secondary me-2 mb-2"
            onClick={() =>
              handleInsertTemplate(
                `<a href="YOUR_URL_HERE" target="_blank" class="btn btn-primary">Button Text</a>`
              )
            }
            disabled={isLoading}
          >
            Insert Button
          </button>
          <button
            className="btn btn-sm btn-outline-secondary me-2 mb-2"
            onClick={() =>
              handleInsertTemplate(`<div class="d-flex justify-content-center my-3">
  <a href="URL1" target="_blank" class="btn btn-primary mx-2">Button 1</a>
  <a href="URL2" target="_blank" class="btn btn-secondary mx-2">Button 2</a>
</div>`)
            }
            disabled={isLoading}
          >
            Insert Button Group
          </button>
          <button
            className="btn btn-sm btn-outline-secondary me-2 mb-2"
            onClick={() =>
              handleInsertTemplate(
                `<h3 class="text-center my-3">Your Heading Here</h3>`
              )
            }
            disabled={isLoading}
          >
            Insert Heading
          </button>
          <button
            className="btn btn-sm btn-outline-secondary me-2 mb-2"
            onClick={() =>
              handleInsertTemplate(`<div class="card my-3">
  <div class="card-body">
    <h5 class="card-title">Card Title</h5>
    <p class="card-text">Some quick example text for the card content.</p>
    <a href="#" class="btn btn-primary">Go somewhere</a>
  </div>
</div>`)
            }
            disabled={isLoading}
          >
            Insert Card
          </button>
        </div>

        {isLoading && (
          <div className="text-center mt-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicBoxEditor;
